// Short links (#734): a bare link in message text with a long query string or fragment is drawn short, so a pasted gift-article
// token or a tracking tail does not fill the screen. Display only: a tap, Copy link and the stored text keep the full link.
//
//   https://www.amazon.com/dp/B07NLD18RW?lv=…&rsd=…&edk=…   ->   🔗 https://www.amazon.com/dp/B07NLD18RW?… ✂️
//
// The rule: shorten when everything from the first "?" or "#" to the end of the link (the query and fragment, the mark
// counted) is over SHORT_URL.limit characters. The short form is the icon, the link up to and with that mark, "…", and the
// scissors. A no-break space (U+00A0) joins the three, so they wrap as one string and the icon never stands alone on a line;
// the web also sets `word-break: break-all` on the link, and the native clients wrap by character inside it.
//
// Not shortened: a link in code (it never reaches the linkifier), a link-preview card, a scheme other than http(s), a link at
// or under the limit, and the address of a `[words](address)`, which is plain text with a bare link in it (#446): a reader
// of those words always sees where the link goes. `shared/cases/short-url.json` holds the cases every client runs.
import { splitLinks, type TextPart } from "./links.ts";

/** The numbers and the marks, in one block. */
export const SHORT_URL = {
  /** Shorten when the query and fragment, mark included, are over this many characters. */
  limit: 40,
  icon: "🔗",
  scissors: "✂️",
  /** Joins the icon, the address and the scissors. */
  glue: " ",
} as const;

/** A link as typed, drawn short, or null when it is shown as typed. */
export function shortUrl(shown: string): string | null {
  const q = shown.indexOf("?");
  const h = shown.indexOf("#");
  const at = q < 0 ? h : h < 0 ? q : Math.min(q, h);
  if (at < 0 || shown.length - at <= SHORT_URL.limit) return null;
  const { glue } = SHORT_URL;
  return `${SHORT_URL.icon}${glue}${shown.slice(0, at + 1)}…${glue}${SHORT_URL.scissors}`;
}

/** What VoiceOver and screen readers say for a short link. */
export function shortLinkLabel(href: string): string {
  let host = href;
  try {
    host = new URL(href).hostname;
  } catch {
    // keep the text
  }
  return `Link to ${host}, shortened`;
}

export type ShortPart = TextPart | (Extract<TextPart, { kind: "link" }> & { short?: string });

/** `splitLinks`, with `short` set on each link that is drawn short. With `on` false (the setting is off) none is. */
export function splitLinksShort(text: string, on: boolean): ShortPart[] {
  const parts = splitLinks(text);
  if (!on) return parts;
  return parts.map((p, i) => {
    if (p.kind !== "link") return p;
    const before = parts[i - 1];
    const after = parts[i + 1];
    const masked = before?.kind === "text" && before.text.endsWith("](") && after?.kind === "text" && after.text.startsWith(")");
    const short = masked ? null : shortUrl(p.text);
    return short ? { ...p, short } : p;
  });
}
